import type { Strings } from "./en";

export const ko: Strings = {
  nav: {
    pronos: "픽",
    standings: "순위",
    shop: "상점",
    profile: "프로필",
    league: "플레이",
  },

  login: {
    chooseNickname: "닉네임을 선택하세요",
    playerNamePlaceholder: "플레이어 이름",
    playAsGuest: "게스트로 플레이",
    joiningGame: "게임 입장 중…",
    or: "또는",
    continueWithGoogle: "Google로 계속하기",
    continueWithEmail: "이메일로 계속하기",
    nicknameChosenAfterSignIn: "로그인 후 닉네임을 선택합니다.",

    emailPlaceholder: "your@email.com",
    invalidEmail: "유효한 이메일을 입력하세요.",
    sendFailed: "전송 실패. 다시 시도하세요.",
    sending: "전송 중…",
    receiveCode: "코드 받기 →",
    codeSentTo: (email: string) => `${email}로 코드를 전송했습니다`,
    enterCodePlaceholder: "코드 입력",
    enterCodeFromEmail: "이메일에서 받은 코드를 입력하세요.",
    invalidCode: "코드가 잘못되었거나 만료되었습니다. 다시 시도하세요.",
    verifying: "확인 중…",
    signIn: "로그인 →",
    back: "← 뒤로",

    atLeast3Chars: "최소 3자 이상 입력하세요.",
    max14Chars: "최대 14자까지 가능합니다.",
    invalidChars: "영문자, 숫자, . _ 및 한글만 사용 가능합니다.",
  },

  nicknameSetup: {
    signedInChooseNickname: "로그인 완료! 게임 닉네임을 선택하세요.",
    nicknameLabel: "닉네임",
    playerNamePlaceholder: "플레이어 이름",
    creating: "계정 생성 중...",
    startPlaying: "시작하기 →",
  },

  recover: {
    title: "다시 오신 것을 환영합니다!",
    foundAccount: (name: string) =>
      `이 기기에서 “${name}” 계정을 찾았습니다. 아직 이 Google 계정에 연결되어 있지 않습니다.`,
    connectButton: (name: string) => `제 계정입니다 — “${name}” 연결하기`,
    connecting: "계정 연결 중…",
    notMineButton: "제 계정이 아닙니다 — 새 계정 만들기",
    restoreSession: "← 이전 세션으로 돌아가기",
  },

  pronos: {
    title: "2026 월드컵",
    today: "오늘",
    tomorrow: "내일",
    finishedCount: (n: number) => `✅ 종료 (${n})`,
    noFinishedMatches: "아직 종료된 경기가 없습니다.",
    draw: "무승부",
    matchFinished: "경기 종료",
    myPredictionLabel: "내 픽",
    noBetPlaced: "픽 없음",
    liveBetsClosed: "라이브 — 배팅 마감",
    betsClosed: "배팅 마감",
  },

  classement: {
    title: "순위",
    refresh: "새로고침",
    picksTab: "🎯 픽 포인트",
    duelTab: "⚔️ 듀얼",
    stadiumTab: "🏟️ 스타디움",
    globalTab: "🌍 전체",
    friendsTab: (n: number) => `👥 친구 (${n})`,
    noFriendsYet: "아직 친구가 없습니다. 전체 탭에서 팔로우해 보세요!",
    makeFirstPredictions: "첫 번째 픽을 등록하여 순위에 참여하세요!",
    noFiveDuelPlayers: "듀얼 랭킹이 없습니다. 플레이하여 경쟁하세요!",
    noStadiumPlayers: "아직 스타디움 트라이얼 도전자가 없습니다. 플레이하여 티어를 차지하세요!",
    tierBadge: "티어",
    stageBest: (n: number) => `최고: 스테이지 ${n}/10`,
    youBadge: "나",
    kLeagueTrophiesTooltip: "K리그 트로피",
    max: "MAX",
    level: (n: number) => `Lv.${n}`,
    points: "포인트",
    statistics: "통계",
    following: "✓ 팔로우 중",
    follow: "+ 팔로우",
    close: "닫기",
    winrate: "적중률",
    pronos: "픽",
    power: "파워",
  },

  shop: {
    title: "상점",
    balanceLabel: (n: number) => `잔액: ${n.toLocaleString("ko-KR")} 코인`,

    standardBoxTitle: "일반 아이템 박스",
    standardBoxSubtitle: "일반~레전더리 등급의 랜덤 장비 1개",
    premiumBoxTitle: "프리미엄 아이템 박스",
    premiumBoxSubtitle: "더 높은 드롭률, 레어 이상 보장",
    cosmeticBoxTitle: "코스메틱 박스",
    cosmeticBoxSubtitle: "랜덤 코스메틱 장비 세트 1개",

    openButton: "열기",

    freeAvailable: "🎁 무료 개봉 가능, 탭하여 받기",
    nextFreeIn: (time: string) => `다음 무료 박스까지 ${time}`,
    freeAtLevel: (n: number) => `🎁 레벨 ${n} 무료, 탭하여 받기`,
    nextFreeAtLevel: (n: number) => `레벨 ${n}에서 다음 무료 박스`,

    footerNote: "픽을 맞히면 코인을 획득합니다. 무료 박스는 매일 초기화됩니다.",

    tapToOpen: "탭하여 열기",
    revealContinue: "계속",

    adminTitle: "🛠 관리자, 아이템 추가",
    adminDropButton: "인벤토리에 추가",
  },

  profil: {
    settingsTitle: "계정",
    guestAccount: "게스트 계정",
    linkedAccount: "연동 계정",
    signOut: "로그아웃",
    signOutConfirmTitle: "로그아웃하시겠습니까?",
    signOutConfirmSub: "게스트 계정은 모든 진행상황이 영구적으로 삭제됩니다.",
    signOutConfirmBtn: "네, 로그아웃",
    cancel: "취소",

    saveModalTitle: "진행 상황 저장",
    saveModalSubtitle: "계정을 연동하여 데이터를 잃지 마세요.",
    withGoogle: "Google로",
    continueWithEmail: "이메일로 계속하기",

    saveProgressTitle: "진행 상황 저장하기",
    saveProgressSubtitle: "계정을 연동하여 데이터를 안전하게 보관하세요.",
    save: "저장",

    statPoints: "포인트",
    statWinrate: "적중률",
    statPicks: "픽",
    statPower: "파워",
    levelLabel: "레벨",

    equipmentTab: "⚔️ 장비",
    statisticsTab: "📊 통계",

    inventoryTitle: "인벤토리",
    autoEquip: "자동 장착",
    allEquipped: "모든 아이템 장착 완료. 팩을 열어 더 얻으세요.",

    equipmentStatsTitle: "장비 스탯",
    equipmentStatsSub: "장착 아이템 기준",
    radarLabel: "레이더",
    injuryReduction: "부상 감소",
    statFromGear: "장비",
    statFromLevel: "레벨",

    recentPicksTitle: "최근 픽",
    drawPick: "무승부",

    rankLegendaire: "레전더리",
    rankElite: "엘리트",
    rankExpert: "엑스퍼트",
    rankPro: "프로",
    rankRookie: "루키",

    sandboxLabel: "🧪 샌드박스, aseo.bonjour에게만 표시",
    testLevelUp: "⬆️ 레벨업",
    testBetResult: "🎲 픽 결과",

    changeAvatarTooltip: "아바타 변경",
  },

  betResult: {
    matchCount: (n: number) => `${n}경기`,
    resultsTitle: "픽 결과",
    continue: "계속",

    vs: "vs",
    myPrediction: (score: string) => `내 픽: ${score}`,

    yourRewards: "획득 보상",
    accept: "확인",
  },

  levelUp: {
    levelUpLabel: "레벨 업!",
    levelReached: (n: number) => `${n}`,
    ticketEarned: "+1 로지 티켓!",
    ticketDropNote: "드롭률 10%, 행운이네요!",
    noTicketNote: "이번엔 티켓이 없네요. 레벨업마다 10% 확률로 획득 기회가 있으니 계속 플레이하세요.",
    continueButton: "계속",
  },

  itemModal: {
    sell: "판매",
    sellConfirmTitle: "이 아이템을 판매하시겠습니까?",
    sellConfirmSub: "이 작업은 취소할 수 없습니다.",
    unequip: "해제",
    equip: "장착",
    total: "합계",
    defense: "방어",
    cancel: "취소",
    same: "동일",
  },

  items: {
    slots: {
      head: "머리",
      top: "유니폼",
      shoes: "신발",
      ball: "공",
      background: "배경",
      familiar: "동반자",
    },

    stats: {
      technique: "기술",
      defense: "방어",
      physique: "체력",
      vitesse: "생명력",
      blessure: "부상 감소",
      energy: "마나",
    },

    statsShort: {
      TEC: "기술",
      DEF: "방어",
      PHY: "체력",
      VIT: "생명력",
    },

    rarity: {
      commun: "일반",
      rare: "레어",
      epic: "에픽",
      ultra: "레전더리",
    },

    sellFor: (n: number) => `${n}에 판매`,
    emptySlot: "비어 있음",
    maxLevel: "MAX",
  },

  itemCard: {
    emptySlot: "비어 있음",
  },

  leagueJoin: {
    back: "← 뒤로",
    createTeamTitle: "팀 만들기",
    createTeamSubtitle: "팀은 다른 플레이어에게 공개됩니다. 기억하기 쉬운 이름을 선택하세요.",
    teamNamePlaceholder: "팀 이름...",
    createAndJoin: "만들고 참가 →",

    communityLeagueTitle: "커뮤니티 리그",
    communityLeagueDesc: "팀에 합류하여 파워를 합치고 봇 상대와 경쟁하세요. 하루 1경기, 매주 승격 및 강등.",

    joinTeamHeader: "팀 참가",
    createTeamButton: "+ 팀 만들기",
    joinArrow: "참가 →",
    teamMeta: (players: number, power: number) => `${players}명 · ⚡ ${power} 파워`,
  },

  leagueHub: {
    fiveDuelSubtitle: "랭크 1v1 듀얼 — 나만의 빌드와 스펠",
    comingSoon: "출시 예정",
    teamLeagueSubtitle: "친구들과 함께 리그를 올라가세요",
    stadiumTrialSubtitle: "Solo PVE · 10 stages · Auto-combat",
  },

  leagueRatings: {
    victory: "승리!",
    draw: "무승부",
    defeat: "패배",
    mvpBadge: "MVP",
    meBadge: "나",
    continue: "계속",
  },

  leagueSettings: {
    title: "리그 설정",
    currentTeamLabel: "현재 팀",
    leaveTeam: "팀 탈퇴",
    leaveConfirmMsg: "정말로 탈퇴하시겠습니까? 팀 선택 화면으로 돌아갑니다.",
    confirmLeave: "네, 탈퇴합니다",
    cancel: "취소",
    close: "닫기",
  },

  leagueElo: {
    title: "리그 등급",
    subtitle: "매주 그룹 1위를 달성하면 리그가 올라갑니다.",
    currentBadge: "현재",
    minElo: (n: number) => `${n}+ ELO`,
    close: "닫기",
  },

  leagueOverview: {
    title: "리그",
    prepareMatches: "경기 준비",
    preparedRatio: (done: number, total: number) => `${done}/${total}`,

    assetsOnField: "🃏 필드 위의 자산",
    leagueGolds: "💰",
    noAssetsYet: "아직 아무도 자산을 필드에 보내지 않았습니다.",
    meBadge: "나",

    standingsTab: "🏆 순위",
    scheduleTab: "📅 경기 일정",

    rankColumn: "#",
    teamColumn: "팀",
    playedColumn: "경기",
    wdlColumn: "승-무-패",
    ptsColumn: "점수",

    standingsTitle: (rank?: number) => rank != null ? `🏆 순위 · #${rank}` : "🏆 순위",
    meBadgeInline: "나",

    roundLabel: (n: number) => `${n}라운드`,
    fullTime: "종료",
    matchesPlayed: "경기 수",
    vs: "VS",
    win: "승",
    loss: "패",
    draw: "무",
    startsIn: (h: number, m: string) => `${h}시간 ${m}분 후 시작`,

    played: "완료",
    ready: "준비됨",
    prepare: "준비",

    backToLeagueOverview: "← 리그 개요",

    communityLeagueLabel: "커뮤니티 리그",
    rosterLabel: "👥 로스터",
    botGkLevel: (n: number) => `봇 골키퍼 — 레벨 ${n}`,
    gkUpgradeHint: "준비 탭에서 업그레이드 가능",
    teamComposition: (p: number, b: number) => `${p}명 · ${b}개 봇`,
    matchesPlayedCount: (n: number) => `${n}경기 완료`,
    winsPercent: (n: number) => `${n}% 승률`,
  },

  leaguePrep: {
    shopLockedUntilTomorrow: "🔒 내일까지 잠김",
    resetsIn: (time: string) => `${time} 후 초기화`,
  },

  orbFusion: {
    back: "← 준비",
    title: "오브 합성",

    powerUnlocked: "파워 해제됨",
    findItInPrep: "준비 페이지에서 확인하세요.",
    nice: "좋아요!",

    craft: "제작",
    notEnoughResources: "재료가 부족합니다",
    locked: "잠김",

    specialRecipesTitle: "특별 레시피",
    bettingPointsDisplay: (pts: number) => `🏆 ${pts.toLocaleString("ko-KR")} pts`,
    specialRecipesSubtitle: "스포츠 픽 포인트를 획득하여 해금하세요.",

    toUnlock: "· 해금 필요",
    levelUp: (n: number, effect: string) => `· Lv. ${n}: ${effect}`,
    ptsLabel: (pts: string) => `${pts} pts`,

    lockedLabel: (pts: string) => `🔒 ${pts} pts`,

    levelBadge: (n: number) => `Lv. ${n}`,

    effects: {
      precision: "+{pct}% 정확도",
      tackle_safety: "-{pct}% 파울",
      goalkeeper_buff: "+{pct}% 골키퍼 강화",
      counter_chance: "+{pct}% 역습",
      build_up: "+{pct}% 빌드업",
      enemy_debuff: "-{pct}% 상대 공격력",
    },
  },

  matchResult: {
    win: "승",
    draw: "무",
    loss: "패",
  },

  matchSheet: {
    back: "← 뒤로",
    title: "경기 기록",
    sandboxMode: "🧪 샌드박스 — 테스트 시뮬레이션",
    noSpell: "스킬 없음",
    runTest: "▶ 테스트 경기 실행",
    runTestHint: "경기를 실행하여 엔진 결과를 확인하세요.",
    statsTitle: "통계",
    highlightsTitle: "주요 장면",
    debugTitle: "🧪 엔진 디버그",
    debugLog: (n: number) => `시퀀스 로그 (${n})`,
    debugCurve: "파워 곡선: 0.85 − 0.35×(def/att)^1.65 · 범위 [15%–85%] · 골키퍼 [42%–91%]",
    spellsTitle: "사용된 스킬",
    noSpellUsed: "이 경기에서 사용된 스킬 없음.",

    statPossession: "점유율",
    statShots: "슈팅",
    statShotsOnTarget: "유효 슈팅",
    statSaves: "선방",
    statPasses: "패스",
    statPassAccuracy: "패스 정확도",
    statInterceptions: "인터셉트",
    statDribbles: "드리블",
    statTackles: "태클",
    statCounters: "역습",

    outcomeGoal: "골",
    outcomeShotSaved: "골키퍼 선방",
    outcomeShotOff: "빗나감",
    outcomeTackle: "태클",
    outcomeInterception: "인터셉트",
    outcomeCounter: "→ 역습",
    outcomeBuildup: "점유",

    controlLabel: "컨트롤 (점유율)",
    inspectTitle: "1v1 대결 분석",
    rawStats: "원시 스탯",
    dribVsTackle: "드리블 vs 태클",
    shotVsBlock: "슈팅 vs 블록",
    dribVsTackleCounter: "드리블 vs 태클 (역습)",
    shotVsBlockCounter: "슈팅 vs 블록 (역습)",
    gkFormula: (prob: string, keeper: string, finish: string, min: string, max: string) =>
      `${prob} (골키퍼 ${keeper} vs 슈팅 ${finish}, 범위 ${min}–${max}%)`,
    homeSpell: (drib: string, fin: string, tackle: string) => `홈 스킬: ×${drib} 드리블 · ×${fin} 슈팅 · ×${tackle} 태클`,
    awaySpell: (drib: string, fin: string, tackle: string) => `원정 스킬: ×${drib} 드리블 · ×${fin} 슈팅 · ×${tackle} 태클`,

    win: "승리",
    draw: "무승부",
    loss: "패배",

    savedText: "선방",
    goalText: "🚨 골",

    statTechnique: "기술",
    statDefense: "방어",
    statPhysique: "체력",
    statVitesse: "생명력",
  },

  leagueDuel: {
    prepTitle: "준비",
    prepSubtitle: "당신이 주장입니다. 포메이션과 스킬 3개를 선택하세요.",
    energyLabel: (cur: number, max: number) => `⚡ 에너지 ${cur}/${max}`,
    energyFull: "가득 참",
    energyRegen: (time: string) => `${time} 후 +1`,
    compositionLabel: "포메이션",
    spellsLabel: (cur: number, total: number) => `스킬 (${cur}/${total})`,
    launchMatch: "경기 시작 ⚽",
    noEnergy: "⚡ 에너지 부족",

    chestTitle: "오늘의 보상",
    chestClaimed: "이미 받았습니다. 내일 다시 오세요!",
    chestReady: "🎁 보상 준비됨 — 탭하여 열기!",
    chestProgress: (cur: number, goal: number) => `오늘 ${cur}/${goal}경기`,

    goalsBar: "골",
    finished: "경기 종료",
    phase: (cur: number, total: number) => `${cur} / ${total} 페이즈`,
    eventsLabel: "이벤트",
    skipButton: "건너뛰기 ⏭",
    eventGoal: "⚽ 골!",
    eventSave: "🧤 선방!",
    eventInterception: "🛡️ 인터셉트!",
    eventShot: "🎯 강슛!",

    resultWin: "승리 🏆",
    resultLoss: "패배",
    resultDraw: "무승부",
    opponentElo: (elo: number) => `상대 ${elo} ELO`,
    goalsTitle: "골",
    noGoals: "무득점 — 탄탄한 수비.",
    goalEvent: (player: string, phase: number) => `⚽ ${player} — ${phase}페이즈`,
    replay: "다시 하기",
    exit: "나가기",

    formations: {
      "3-1": "수비적",
      "2-2": "균형",
      "1-3": "공격적",
    },

    spells: {
      boost: { name: "스프린트", desc: "선수를 가속시킵니다." },
      wall: { name: "장벽", desc: "수비를 강화합니다." },
      curve: { name: "커브", desc: "막기 어려운 슛." },
      freeze: { name: "빙결", desc: "상대를 느리게 합니다." },
      heal: { name: "두 번째 바람", desc: "피로를 줄입니다." },
      vision: { name: "비전", desc: "더 나은 패스." },
    },

    chestClaimedShort: "수령 완료",
    prepScreenTitle: "솔로 듀얼",
    defensesButton: "🛡️ 수비 기록",
    defensesTitle: "수비 기록",
    defensesSubtitle: "나의 유령 수비에 대한 공격",
    defensesLoading: "불러오는 중…",
    defensesEmpty: "아직 공격 없음",
    defensesEmptyDesc: "아직 아무도 당신의 유령 수비를 공격하지 않았습니다.",
    defenseWon: "수비 성공",
    defenseLost: "돌파됨",
    captainBadge: "주장",
    energyNextIn: (time: string) => `${time} 후 에너지 +1`,
    squadTitle: "내 팀",
    squadSubtitle: "이 팀원들은 당신이 모집하고 관리합니다",
    formationTitle: "포메이션",
    maxRank: "최고 랭크",
    findOpponent: "상대 찾기 · 1⚡",
    findingOpponent: "🔍 상대 찾는 중…",
    creatingMatch: "⚽ 경기 생성 중…",
    timeJustNow: "방금",
    timeMinutesAgo: (m: number) => `${m}분 전`,
    timeHoursAgo: (h: number) => `${h}시간 전`,
    timeDaysAgo: (d: number) => `${d}일 전`,
    reputation: "평판",
    newRankLabel: (rank: string) => `🏅 새 랭크: ${rank}`,
    continueButton: "계속하기",
    goldenGoal: "⚡ 골든 골",
  },

  topBar: {},

  feedback: {
    title: "피드백 보내기",
    subtitle: "버그를 발견했거나 아이디어가 있나요? 알려주세요 — 팀에게 바로 전달됩니다.",
    catBug: "버그",
    catIdea: "아이디어",
    catOther: "기타",
    placeholder: "무엇이든 말씀해 주세요",
    send: "보내기",
    sending: "보내는 중…",
    error: "전송하지 못했습니다 — 다시 시도해 주세요.",
    thanksTitle: "피드백 감사합니다!",
    thanksSub: "게임을 개선하는 데 큰 도움이 됩니다.",
  },

  common: {
    live: "LIVE",
    draw: "무승부",
    vs: "vs",
    close: "닫기",
    cancel: "취소",
    back: "← 뒤로",
    continue: "계속",
    loading: "로딩 중…",
    matchCount: (n: number) => `${n}경기`,
  },
};
